import axios from "axios";
import MapPlugin from "../MapPlugin";
import { Checkbox, FormControlLabel, FormGroup, Stack } from "@mui/material";
import LayerSettings from "../../common/LayerSettings";
import { useCallback, useEffect, useState } from "react";
import { useOperationsMap } from "../../OperationsMapProvider";
import { useTranslation } from "react-i18next";



export default class WeatherLayer extends MapPlugin {

  constructor(map) {
    super("weather", map);

    this.defaultSettings = {
      nowcast: true,
      infrared: false,
    };

    this._timer = null;
  }


  async getSources() {

    const res = await axios("https://api.rainviewer.com/public/weather-maps.json");
    const catalog = res.data;
    const baseUrl = catalog.host;

    const infrared = catalog.satellite.infrared;
    const nowcast = catalog.radar.nowcast;

    const nowcastPath = nowcast[nowcast.length - 1].path;
    const infraredPath = infrared[nowcast.length - 1].path;

    return { nowcastPath, infraredPath, baseUrl };
  }

  async updateSources() {
    if (this.destroyed) return;

    try {
      const { infraredPath, nowcastPath, baseUrl } = await this.getSources();

      this._map.getSource("met-nowcast")?.setTiles([
        `${baseUrl}${nowcastPath}/256/{z}/{x}/{y}/2/1_1.webp`
      ]);

      this._map.getSource("met-infrared")?.setTiles([
        `${baseUrl}${infraredPath}/256/{z}/{x}/{y}/0/1_1.webp`
      ]);

    } catch (err) {
      console.log(err);
    }
  }

  async onMount() {
    try {
      const { infraredPath, nowcastPath, baseUrl } = await this.getSources();

      this._map.addSource("met-nowcast", {
        "type": "raster",
        "tiles": [
          `${baseUrl}${nowcastPath}/256/{z}/{x}/{y}/2/1_1.webp`
        ],
        "tileSize": 256,
        "minzoom": 1,
        "maxzoom": 20,
        "attribution": "<a href=\"https://www.rainviewer.com\">© RainViewer</a>",
        "interval": 600000,
      });

      this._map.addSource("met-infrared", {
        "type": "raster",
        "tiles": [
          `${baseUrl}${infraredPath}/256/{z}/{x}/{y}/0/1_1.webp`
        ],
        "tileSize": 256,
        "minzoom": 1,
        "maxzoom": 20,
        "attribution": "<a href=\"https://www.rainviewer.com\">© RainViewer</a>",
        "interval": 600000,
      });

      // Add the radar layer on top of your map
      this._map.addLayer({
        id: "met-nowcast-layer",
        type: "raster",
        slot: "elevation-2",
        source: "met-nowcast",
        paint: {
          "raster-opacity": 0.7,
        }
      });


      this._map.addLayer({
        id: "met-infrared-layer",
        type: "raster",
        slot: "elevation-2",
        source: "met-infrared",
        paint: {
          "raster-opacity": [
            'interpolate',
            ['linear'],
            ['zoom'],
            0, 0.7,
            18, 0.2
          ],
        }
      });

      this._map.setLayoutProperty("met-nowcast-layer", "visibility", !!this.settings.nowcast ? "visible" : "none");
      this._map.setLayoutProperty("met-infrared-layer", "visibility", !!this.settings.infrared ? "visible" : "none");
      this._timer = setInterval(() => this.updateSources(), 1000 * 60 * 5);

    } catch (err) {
      console.log(err);
    }
  }

  onUnMount() {
    clearInterval(this._timer);
    this._map.removeLayer("met-nowcast-layer");
    this._map.removeLayer("met-infrared-layer");
    this._map.removeSource("met-infrared");
    this._map.removeSource("met-nowcast");
  }

  onUpdateSettings(settings) {
    this._map.setLayoutProperty("met-nowcast-layer", "visibility", !!settings.nowcast ? "visible" : "none");
    this._map.setLayoutProperty("met-infrared-layer", "visibility", !!settings.infrared ? "visible" : "none");
  }
}



export function WeatherLayerSettings() {
  const { t } = useTranslation();
  const { operationsMap } = useOperationsMap();
  const settings = operationsMap.getSettings("weather");

  const [nowcast, setNowcast] = useState(settings.nowcast);
  const [infrared, setInfrared] = useState(settings.infrared);
  const [active, setActive] = useState(operationsMap.isPluginActive("weather"));

  useEffect(() => {
    const fn = () => {
      setNowcast(settings.nowcast);
      setInfrared(settings.infrared);
      setActive(operationsMap.isPluginActive("weather"));
    };

    operationsMap.on("reset", fn);
    return () => operationsMap.off("reset", fn);
  }, [operationsMap, settings]);

  // Handle plugin active state
  const onHandleAccordion = useCallback((_, val) => {
    setActive(val);

    if (val) {
      operationsMap.activatePlugin("weather");
    } else {
      operationsMap.deactivatePlugin("weather");
    }
  }, [operationsMap]);


  const onHandleNowcast = useCallback((ev) => {
    const val = ev.target.checked;
    setNowcast(val);
    operationsMap.updateSettings("weather", { nowcast: val });
  }, [operationsMap]);


  const onHandleInfrared = useCallback((ev) => {
    const val = ev.target.checked;
    setInfrared(val);
    operationsMap.updateSettings("weather", { infrared: val });
  }, [operationsMap]);

  return (
    <LayerSettings title={t("operationsMap:layers:weather:title")} open={active} onChange={onHandleAccordion} last>
      <Stack spacing={1}>

        <FormGroup>
          <FormControlLabel control={<Checkbox checked={nowcast} onChange={onHandleNowcast} />} label={t("operationsMap:layers:weather:showNowcast")} />
          <FormControlLabel control={<Checkbox checked={infrared} onChange={onHandleInfrared} />} label={t("operationsMap:layers:weather:showInfrared")} />
        </FormGroup>

      </Stack>
    </LayerSettings>
  );
}
